import type { FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import FilterChips, { type Params as FilterChipsParams } from 'Components/filterChips/index';
import Loading from 'Components/smallComponents/loading';
import type {
  TemplateContextMap,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { discardManyFields, resetRequest } from 'Core/actions/request';
import { isVehicleSelectedSelector } from 'Core/selectors/fitmentSearch/index';
import { createShouldShowFacetPanelSelector } from 'Core/selectors/search';
import { resultsLoadingSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';
import safeTemplateCall from 'Utils/safeTemplateCall';

import { FacetsPanelConfig } from './common/index';
import Facets, { type Params as FacetsParams } from './facets';

export type Params = {
  filterChips: TemplateFunctionInvoker<FilterChipsParams>;
  facets: TemplateFunctionInvoker<FacetsParams>;
  clear: () => void;
  isVehicleSelected: boolean;
} & TemplateContextMap;

export interface Props {
  template: TemplateFunction<Params>;
  name: string;
  fields?: string[];
  ignoreFields?: string[];
  showAllAlways?: true;
  disableCollapse?: true;
  initCollapsed?: boolean;
  initCollapsedFacets?: string[];
  initExpandedFacets?: string[];
  extraParams?: TemplateContextMap;
  initHidden?: boolean;
  valuesCountToShowFilterInput?: number;
}

const FacetPanel: FunctionComponent<Props> = ({
  template,
  name: facetPanelName,
  fields,
  ignoreFields,
  showAllAlways,
  disableCollapse,
  initCollapsed,
  initCollapsedFacets,
  initExpandedFacets,
  extraParams,
  initHidden,
  valuesCountToShowFilterInput = 30,
}) => {
  const dispatch = useDispatch();

  const isVehicleSelected = useSelector(isVehicleSelectedSelector);

  const showPanel = useShowPanel(fields, ignoreFields);

  if (initHidden && !showPanel) {
    return null;
  }

  const appendedClasses = !showPanel ? 'cm_empty' : '';

  const startOver = () => dispatch(resetRequest());
  const clearPanel = () => fields && dispatch(discardManyFields(fields));
  const onClick = simpleHandler({ startOver, clearPanel });

  const filterChips = (templ: TemplateFunction<FilterChipsParams>) => {
    const props = { template: templ, fields, ignoreFields };
    return (<FilterChips {...props} key="chips" />) as TemplateResult;
  };

  const facets = (templ: TemplateFunction<FacetsParams>) => {
    const props = { template: templ, fields, ignoreFields };
    return (<Facets {...props} key="facets" />) as TemplateResult;
  };

  const loading = !initHidden && <Loading selector={resultsLoadingSelector} key="loading" />;

  const component = safeTemplateCall(
    template,
    {
      filterChips,
      facets,
      ...extraParams,
      isVehicleSelected,
      clear: startOver,
    },
    'FacetPanel',
  );
  const facetPanelContext = {
    facetPanelName,
    showAllAlways,
    disableCollapse,
    initCollapsed,
    initCollapsedFacets,
    initExpandedFacets,
    valuesCountToShowFilterInput,
  };
  return (
    <FacetsPanelConfig.Provider value={facetPanelContext}>
      {cloneSafe(component, null, { onClick, appendedClasses, appendedChildren: loading })}
    </FacetsPanelConfig.Provider>
  );
};

export default FacetPanel;

function useShowPanel(fields?: string[], ignoreFields?: string[]) {
  return useSelector(
    useMemo(() => createShouldShowFacetPanelSelector(fields ?? [], ignoreFields), [fields, ignoreFields]),
  );
}
